// Category management (marker + shape categories), rendered into a container.
import { api } from "../api";
import { config, reloadConfig, KIND_LABEL_PLURAL } from "../catalog";
import { createImageField } from "./imagefield";
import { confirmDialog } from "./prompt";
import type { GlyphShape, MarkerCategory, ShapeCategory, ShapeKind } from "../types";

const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

let host: HTMLElement | null = null;

/** Render the category-management UI into `container` (used by the Settings tab). */
export function renderCategorySettings(container: HTMLElement): void {
  host = container;
  container.innerHTML = `
    <section class="settings-block">
      <h3>Objekt-Kategorien</h3>
      <div data-list="markers"></div>
    </section>
    <section class="settings-block">
      <h3>Flächen-Kategorien</h3>
      <div data-list="shapes"></div>
    </section>`;
  render();
}

async function afterChange(): Promise<void> {
  await reloadConfig();
  render();
}

function render(): void {
  if (!host) return;
  renderMarkers(host.querySelector<HTMLElement>('[data-list="markers"]')!);
  renderShapes(host.querySelector<HTMLElement>('[data-list="shapes"]')!);
}

/* --------------------------- marker categories --------------------------- */

function shapeToggle(value: GlyphShape): HTMLElement {
  const seg = document.createElement("div");
  seg.className = "seg";
  seg.innerHTML = `
    <button type="button" data-shape="circle" class="${value === "circle" ? "active" : ""}">● Kreis</button>
    <button type="button" data-shape="rect" class="${value === "rect" ? "active" : ""}">▮ Rechteck</button>`;
  seg.querySelectorAll("button").forEach((b) => {
    b.addEventListener("click", () => {
      seg.querySelectorAll("button").forEach((x) => x.classList.remove("active"));
      b.classList.add("active");
    });
  });
  return seg;
}
const shapeOf = (seg: HTMLElement): GlyphShape =>
  seg.querySelector("button.active")?.getAttribute("data-shape") === "rect" ? "rect" : "circle";

function renderMarkers(host: HTMLElement): void {
  host.replaceChildren();

  for (const cat of config().markerCategories) {
    host.appendChild(markerRow(cat));
  }

  const add = document.createElement("div");
  add.className = "cat-add";
  add.innerHTML = `
    <input type="text" data-f="label" placeholder="Neue Objekt-Kategorie…" />
    <input type="color" data-f="color" value="#7a3f8a" />`;
  const seg = shapeToggle("circle");
  add.appendChild(seg);
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn sm primary";
  btn.textContent = "Anlegen";
  add.appendChild(btn);
  btn.addEventListener("click", async () => {
    const label = add.querySelector<HTMLInputElement>('[data-f="label"]')!.value.trim();
    const color = add.querySelector<HTMLInputElement>('[data-f="color"]')!.value;
    if (!label) return;
    btn.disabled = true;
    try {
      await api.createMarkerCategory({ label, color, shape: shapeOf(seg) });
      await afterChange();
    } finally {
      btn.disabled = false;
    }
  });
  host.appendChild(add);
}

function markerRow(cat: MarkerCategory): HTMLElement {
  const row = document.createElement("div");
  row.className = "cat-row";
  row.innerHTML = `
    <span class="cat-swatch ${cat.shape === "rect" ? "sq" : ""}" style="background:${cat.color}"></span>
    <span class="cat-label">${esc(cat.label)}</span>
    <span class="cat-meta">${cat.shape === "rect" ? "Rechteck" : "Kreis"}${cat.imageUrl ? " · Bild" : ""}${cat.builtin ? " · Standard" : ""}</span>
    <button type="button" class="btn sm ghost" data-a="edit">Bearbeiten</button>
    <button type="button" class="btn sm ghost" data-a="del" style="color:var(--danger)">Löschen</button>`;

  row.querySelector('[data-a="del"]')!.addEventListener("click", async () => {
    const ok = await confirmDialog(
      "Kategorie löschen",
      `„${cat.label}" löschen? Bereits platzierte Objekte dieser Kategorie werden dann grau dargestellt.`,
      { confirmLabel: "Löschen" }
    );
    if (!ok) return;
    await api.deleteMarkerCategory(cat.id);
    await afterChange();
  });

  row.querySelector('[data-a="edit"]')!.addEventListener("click", () => {
    if (row.nextElementSibling?.classList.contains("cat-editor")) {
      row.nextElementSibling.remove();
      return;
    }
    row.after(markerEditor(cat));
  });
  return row;
}

function markerEditor(cat: MarkerCategory): HTMLElement {
  const box = document.createElement("div");
  box.className = "cat-editor";
  box.innerHTML = `
    <div class="field-row">
      <div class="field"><label>Name</label><input type="text" data-f="label" value="${esc(cat.label)}"></div>
      <div class="field" style="flex:0 0 70px;"><label>Farbe</label><input type="color" data-f="color" value="${cat.color}"></div>
    </div>
    <div class="field"><label>Form auf dem Plan</label></div>`;
  const seg = shapeToggle(cat.shape);
  box.querySelector('.field:last-of-type')!.appendChild(seg);

  const imgLabel = document.createElement("div");
  imgLabel.className = "field";
  imgLabel.innerHTML = "<label>Referenzbild (erscheint in der Druck-Legende)</label>";
  let imageFile: string | null | undefined = undefined;
  imgLabel.appendChild(
    createImageField({
      url: cat.imageUrl,
      onChange: (file) => {
        imageFile = file;
      },
    })
  );
  box.appendChild(imgLabel);

  const actions = document.createElement("div");
  actions.className = "edit-actions";
  actions.innerHTML = `<button type="button" class="btn sm primary" data-a="save">Speichern</button>
    <button type="button" class="btn sm ghost" data-a="cancel">Schließen</button>`;
  box.appendChild(actions);

  actions.querySelector('[data-a="cancel"]')!.addEventListener("click", () => box.remove());
  actions.querySelector('[data-a="save"]')!.addEventListener("click", async () => {
    await api.updateMarkerCategory(cat.id, {
      label: box.querySelector<HTMLInputElement>('[data-f="label"]')!.value.trim() || cat.label,
      color: box.querySelector<HTMLInputElement>('[data-f="color"]')!.value,
      shape: shapeOf(seg),
      ...(imageFile === undefined ? {} : { imageFile }),
    });
    box.remove();
    await afterChange();
  });
  return box;
}

/* --------------------------- shape categories --------------------------- */

function renderShapes(host: HTMLElement): void {
  host.replaceChildren();
  const cfg = config().shapeCategories;
  const groups: Array<{ title: string; kind: ShapeKind; cats: ShapeCategory[]; placeholder: string }> = [
    {
      title: "Flächen",
      kind: "object",
      cats: [...(cfg.area ?? []), ...(cfg.object ?? [])],
      placeholder: "Neue Kategorie… (z. B. Lounge)",
    },
    { title: "Tensator-Absperrungen", kind: "line", cats: cfg.line ?? [], placeholder: "Neue Kategorie…" },
  ];

  for (const g of groups) {
    const group = document.createElement("div");
    group.className = "cat-group";
    group.innerHTML = `<h4>${g.title}</h4>`;
    for (const cat of g.cats) group.appendChild(shapeRow(cat));

    const add = document.createElement("div");
    add.className = "cat-add";
    add.innerHTML = `
      <input type="text" data-f="label" placeholder="${g.placeholder}" />
      <input type="color" data-f="color" value="#2f6f8a" />
      <button type="button" class="btn sm primary" data-a="add">Anlegen</button>`;
    add.querySelector('[data-a="add"]')!.addEventListener("click", async () => {
      const label = add.querySelector<HTMLInputElement>('[data-f="label"]')!.value.trim();
      const color = add.querySelector<HTMLInputElement>('[data-f="color"]')!.value;
      if (!label) return;
      await api.createShapeCategory({ kind: g.kind, label, color });
      await afterChange();
    });
    group.appendChild(add);
    host.appendChild(group);
  }
}

function shapeRow(cat: ShapeCategory): HTMLElement {
  const row = document.createElement("div");
  row.className = "cat-row";
  row.innerHTML = `
    <span class="cat-swatch sq" style="background:${cat.color}"></span>
    <input type="text" class="cat-inline" data-f="label" value="${esc(cat.label)}" />
    <input type="color" data-f="color" value="${cat.color}" />
    <button type="button" class="btn sm ghost" data-a="save">Speichern</button>
    <button type="button" class="btn sm ghost" data-a="del" style="color:var(--danger)">Löschen</button>`;

  row.querySelector('[data-a="save"]')!.addEventListener("click", async () => {
    await api.updateShapeCategory(cat.id, {
      label: row.querySelector<HTMLInputElement>('[data-f="label"]')!.value.trim() || cat.label,
      color: row.querySelector<HTMLInputElement>('[data-f="color"]')!.value,
    });
    await afterChange();
  });
  row.querySelector('[data-a="del"]')!.addEventListener("click", async () => {
    const ok = await confirmDialog("Kategorie löschen", `„${cat.label}" löschen?`, {
      confirmLabel: "Löschen",
    });
    if (!ok) return;
    await api.deleteShapeCategory(cat.id);
    await afterChange();
  });
  return row;
}
